import { useEffect, useRef, useSyncExternalStore } from 'react';
import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import { subscribeSession } from '@/auth/session';
import { useRoleSwitchProgress } from '@/auth/roleSwitchProgress';
import { dataState, statusOfAnswer } from '@/components/shared/AccessState/accessAnswer';
import { ApiProvider } from '@/provider/ApiProvider';
import { activeAssignmentId, notificationKeys } from '@/query/keys';
import { queryClient } from '@/query/queryClient';
import {
  AccessAnswerError,
  type AccessQueryState,
  PAGE_DATA_GC_TIME,
  retryAccessQuery,
} from '@/query/useAccessQuery';

import {
  type NotificationItem,
  type NotificationPage,
  type NotificationView,
  type ServiceAnswer,
  getNotificationSummary,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  notificationApiUrl,
} from './api';
import { isUnread } from './format';

/** The bell asks again every minute while the tab is visible (and on focus). */
export const SUMMARY_POLL_MS = 60_000;
/** A shown list (the Notifications page, the open panel) asks again every 15 s (tab visible). */
export const LIST_POLL_MS = 15_000;
/** ... and every 5 s while one of its loaded items is in progress, so the bar moves. */
export const PROGRESS_POLL_MS = 5_000;
/** A focus right after an answer does not ask again. */
const FOCUS_MIN_GAP_MS = 5_000;
/** Statuses of a service that has no notification centre (yet): no bell, no pages. */
export const ABSENT_STATUSES = [404, 501];

type SummaryData = { available: true; unread: number; needs_action: number } | { available: false };

/**
 * - `absent`: no backend (VITE_NOTIFICATION_API_URL unset, or the summary answers 404 / 501):
 *   no bell, and the routes show the not-found page;
 * - `available`: the summary answered;
 * - `unknown`: no answer yet;
 * - `error`: the summary failed otherwise (the pages still open and show their own state).
 */
export type NotificationAvailability = 'unknown' | 'available' | 'absent' | 'error';

const aborted = (): Error => {
  const error = new Error('aborted');
  error.name = 'AbortError';
  return error;
};

/** Reads a failed answer the way useAccessQuery does. */
const failure = (answer: ServiceAnswer<unknown>): AccessAnswerError => {
  const state = dataState(answer);
  const status = statusOfAnswer(answer);
  const answered = status !== null && status > 0 && status < 500;
  return new AccessAnswerError(
    state === 'ok' || state === 'no_data' ? 'error' : state,
    status,
    answered,
  );
};

/** Re-renders when the active assignment changes (the keys follow it). */
const useActiveAssignmentId = (): number | null =>
  useSyncExternalStore(subscribeSession, activeAssignmentId, activeAssignmentId);

/**
 * The bell's summary (`GET me/notifications/summary`) and whether there is a notification
 * centre at all. `poll`: this caller keeps it fresh (every 60 s while the tab is visible and
 * on window focus); only the sidebar bell polls, the other callers read the same cache. No
 * request while a role switch is in progress (auth/roleSwitchProgress.ts): the page reloads
 * after it.
 */
export function useNotificationSummary({ poll = false }: { poll?: boolean } = {}) {
  const assignmentId = useActiveAssignmentId();
  const switching = useRoleSwitchProgress() !== null;
  const configured = notificationApiUrl() !== '';
  const enabled = configured && assignmentId !== null && !switching;
  const queryKey = notificationKeys.summary(assignmentId);

  const query = useQuery<SummaryData, AccessAnswerError>(
    {
      queryKey,
      queryFn: async ({ signal }) => {
        const answer = await getNotificationSummary(signal);
        if (signal.aborted) throw aborted();
        if (answer.success && answer.data) return { available: true, ...answer.data };
        if (answer.status !== undefined && ABSENT_STATUSES.includes(answer.status)) {
          return { available: false };
        }
        throw failure(answer);
      },
      enabled,
      staleTime: 30_000,
      retry: retryAccessQuery,
      networkMode: 'always',
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: poll
        ? (q) => (q.state.data?.available === false ? false : SUMMARY_POLL_MS)
        : false,
      // react-query's focus manager: no interval tick while the tab is hidden
      refetchIntervalInBackground: false,
    },
    queryClient,
  );

  const keyText = JSON.stringify(queryKey);
  useEffect(() => {
    if (!poll || !enabled) return undefined;
    const key = JSON.parse(keyText) as QueryKey;
    const refresh = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const state = queryClient.getQueryState<SummaryData>(key);
      if (state?.data?.available === false) return;
      if (state && Date.now() - state.dataUpdatedAt < FOCUS_MIN_GAP_MS) return;
      void queryClient.refetchQueries({ queryKey: key, exact: true });
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [poll, enabled, keyText]);

  const data = query.data;
  let availability: NotificationAvailability;
  if (!configured || data?.available === false) availability = 'absent';
  else if (data?.available) availability = 'available';
  else if (query.isError) availability = 'error';
  else availability = 'unknown';

  return {
    availability,
    unread: data?.available ? data.unread : 0,
    needsAction: data?.available ? data.needs_action : 0,
  };
}

/** True when a loaded page of the list has an item in progress. */
const hasProgress = (data: InfiniteData<NotificationPage, string | null> | undefined): boolean =>
  Boolean(data?.pages.some((p) => p.items.some((i) => i.state === 'in_progress')));

/** How often a shown list asks again: 5 s with an item in progress, else 15 s. */
const listInterval = (data: InfiniteData<NotificationPage, string | null> | undefined): number =>
  hasProgress(data) ? PROGRESS_POLL_MS : LIST_POLL_MS;

/**
 * One list (panel or page): a view and an area, paged with the `next` cursor. While shown (the
 * page; the panel only while open, as it is disabled when closed) it asks again every 15 s, and
 * every 5 s while one of its items is in progress, so a new item and the bar's steps appear
 * soon. When the bell's unread count goes up (the summary polls every 60 s and on focus) it
 * asks at once. No interval tick while the tab is hidden, no request during a role switch.
 */
export function useNotificationList({
  view,
  area = null,
  limit = 30,
  enabled = true,
}: {
  view: NotificationView;
  area?: string | null;
  limit?: number;
  enabled?: boolean;
}) {
  const assignmentId = useActiveAssignmentId();
  const switching = useRoleSwitchProgress() !== null;
  const configured = notificationApiUrl() !== '';
  const active = enabled && configured && assignmentId !== null && !switching;
  const params = { view, area, limit };
  const query = useInfiniteQuery<
    NotificationPage,
    AccessAnswerError,
    InfiniteData<NotificationPage, string | null>,
    QueryKey,
    string | null
  >(
    {
      queryKey: notificationKeys.list(params, assignmentId),
      queryFn: async ({ pageParam, signal }) => {
        const answer = await listNotifications({ view, area, limit, before: pageParam }, signal);
        if (signal.aborted) throw aborted();
        if (answer.success && answer.data) return answer.data;
        throw failure(answer);
      },
      initialPageParam: null,
      getNextPageParam: (last) => last.next ?? undefined,
      enabled: active,
      staleTime: 0,
      gcTime: PAGE_DATA_GC_TIME,
      retry: retryAccessQuery,
      networkMode: 'always',
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: (q) => listInterval(q.state.data),
      // react-query's focus manager: no interval tick while the tab is hidden
      refetchIntervalInBackground: false,
    },
    queryClient,
  );

  // A higher unread count than the last one seen: something new, so the shown list asks at once
  // (not on the first count, nor on a lower one, e.g. after "Mark all as read").
  const summary = useNotificationSummary();
  const unread = summary.availability === 'available' ? summary.unread : null;
  const lastUnread = useRef<number | null>(null);
  const { refetch } = query;
  useEffect(() => {
    const before = lastUnread.current;
    lastUnread.current = unread;
    if (!active || before === null || unread === null || unread <= before) return;
    void refetch();
  }, [unread, active, refetch]);

  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  let state: AccessQueryState;
  if (query.isError) state = query.error instanceof AccessAnswerError ? query.error.state : 'error';
  else if (query.data) state = items.length > 0 ? 'ok' : 'no_data';
  else state = enabled ? 'loading' : 'idle';

  return {
    state,
    items,
    hasMore: Boolean(query.hasNextPage),
    loadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
  };
}

type ListData = InfiniteData<NotificationPage, string | null>;

/** Every cached list, with its view (from its key). */
const cachedLists = (): { key: QueryKey; view: unknown; data: ListData }[] =>
  queryClient
    .getQueriesData<ListData>({ queryKey: notificationKeys.lists() })
    .filter((entry): entry is [QueryKey, ListData] => Boolean(entry[1]?.pages))
    .map(([key, data]) => ({
      key,
      view: (key[4] as { view?: unknown } | undefined)?.view,
      data,
    }));

const mapLists = (
  // eslint-disable-next-line no-unused-vars
  change: (items: NotificationItem[], view: unknown) => NotificationItem[],
): void => {
  for (const { key, view, data } of cachedLists()) {
    queryClient.setQueryData<ListData>(key, {
      ...data,
      pages: data.pages.map((p) => ({ ...p, items: change(p.items, view) })),
    });
  }
};

const changeSummary = (
  // eslint-disable-next-line no-unused-vars
  change: (s: { unread: number; needs_action: number }) => {
    unread: number;
    needs_action: number;
  },
): void => {
  queryClient.setQueryData<SummaryData>(notificationKeys.summary(), (d) =>
    d && d.available ? { ...d, ...change(d) } : d,
  );
};

/** After a failed write: the server's state again. */
const resync = (): void => {
  void queryClient.invalidateQueries({ queryKey: notificationKeys.all() });
};

/**
 * Opening an item: read at once in every cached list and the bell (optimistic), then
 * `POST me/notifications/{id}/read`; a refused write asks the server again.
 */
export function markRead(item: NotificationItem): void {
  if (!isUnread(item)) return;
  const now = new Date().toISOString();
  mapLists((items) =>
    items.map((i) => (i.id === item.id ? { ...i, read_at: i.read_at ?? now } : i)),
  );
  changeSummary((s) => ({ ...s, unread: Math.max(0, s.unread - 1) }));
  void markNotificationRead(item.id).then((answer) => {
    if (!answer.success) resync();
  });
}

/** "Mark all as read": optimistic, then `POST me/notifications/read-all`. */
export async function markAllRead(): Promise<boolean> {
  const now = new Date().toISOString();
  mapLists((items, view) =>
    view === 'unread' ? [] : items.map((i) => (i.read_at ? i : { ...i, read_at: now })),
  );
  changeSummary((s) => ({ ...s, unread: 0 }));
  const answer = await markAllNotificationsRead();
  if (!answer.success) {
    resync();
    ApiProvider.globalToastMessage(
      'The notifications could not be marked as read. Please try again.',
      5000,
      'error',
    );
  }
  return answer.success;
}
